import _ from "lodash";
import { serializeJDBCConnector, serializeRestConnector } from "../../common/utils";
import {
  FETCH_CONNECTIONS,
  FETCH_CONNECTION,
  ENABLE_CONNECTION,
  CREATE_CONNECTOR,
  SET_LATEST_OAUTH_REDIRECT_LINK,
  DISABLE_CONNECTOR,
  CLEAR_DISABLE_CONNECTOR_RESPONSE,
  CREATE_PUBLIC_CONNECTOR,
  SET_PUBLIC_CHATBOT_SNIPPET_METADATA,
  FETCH_ACTION_FIELDS,
  FETCH_ACTION_METAFIELDS,
  SAVE_ACTION_FIELDS
} from "./types";
import { Analytics, API } from "aws-amplify";
import { ANALYTICS_EVENT } from "../../common/ApmlifyRecordEvents";
import { triggerBotBuilding } from "./intentsActions";

import {getEnvironmentName} from "../../utils"
const environment = getEnvironmentName()
const config = require("../../app_configuration").CONFIGURATION[environment];

const apiRequestFixedParams = {
  Version: config.RESPONSUM_API_VERSION,
  Api: config.RESPONSUM_API,
  AccountType: config.RESPONSUM_API_RESOURCE_TYPE.PRIVATE
};

export const fetchConnections = () => async (dispatch, getState) => {
  const state = getState();
  let data = {
    queryStringParameters: {
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      ApiCall: "ListConnections"
    }
  };

  // NB: If bot has a fallback intent, response.data.common will contain a fake "fallback-intent-setup" connection.
  // This fake connection is used to render the "Setup Error Handling" card on the connections page ("Available
  // Connectors" section).
  const response = await API.get(config.AWS_API_NAME, "connectors", data);

  dispatch({
    type: FETCH_CONNECTIONS,
    payload: response.data
  });
};

export const fetchConnection = connectionName => async (dispatch, getState) => {
  const state = getState();
  let data = {
    queryStringParameters: {
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      ApiCall: "GetConnection",
      Connection: connectionName
    }
  };
  const response = await API.get(config.AWS_API_NAME, "connector", data);

  dispatch({
    type: FETCH_CONNECTION,
    payload: response.data
  });
};

export const enableConnection = (connectionId, redirectUrl) => async (dispatch, getState) => {
  const state = getState();
  let data = {
    body: {
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      ApiCall: "OAuth2Callback",
      Connection: connectionId,
      OAuth2UserRedirectLink: redirectUrl,
      ...apiRequestFixedParams
    }
  };
  const response = await API.post(config.AWS_API_NAME, "connectors/enable", data);
  if (response.status !== "success") {
    const analytics_data = {
      connectionId: connectionId,
      errorMessage: response.message
    };
    Analytics.record(ANALYTICS_EVENT(analytics_data).CONNECTION.error);
  } else {
    const analytics_data = {
      connectionId: connectionId
    };
    Analytics.record(ANALYTICS_EVENT(analytics_data).CONNECTION.success);
  }
  dispatch({
    type: ENABLE_CONNECTION,
    payload: response.status === "error" ? false : true
  });
};

export const createConnector = (clientId, clientSecret, connector, url) => async (dispatch, getState) => {
  const state = getState();
  let serializedData;
  if (connector.type.startsWith("jdbc")) {
    serializedData = serializeJDBCConnector(connector);
  } else {
    serializedData = serializeRestConnector(connector, clientId, clientSecret, url);
  }

  let data = {
    body: {
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      ApiCall: "CreateConnection",
      Skill: state.skill.appId,
      FormParams: serializedData,
      ...apiRequestFixedParams
    }
  };
  let action = {
    type: CREATE_CONNECTOR,
    payload: {}
  };

  try {
    const response = await API.post(config.AWS_API_NAME, "connectors/new", data);
    action.payload.status = 200;
    action.payload.oauthRedirectUrl = response.oauth2_user_redirect_link;
    dispatch(triggerBotBuilding());
    // dispatch(buildBot(state.skill.ID));

    // dispatch(triggerBotBuilding(true));
  } catch (err) {
    action.payload.status = 500;
    action.payload.oauthRedirectUrl = null;
  }

  dispatch(action);
};

export const createPublicRestConnector = props => async (dispatch, getState) => {
  const state = getState();

  let data = {
    body: {
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      ApiCall: "CreatePublicRestConnection",
      TemplateConnection: props.template,
      FormParams:
        "action=" +
        props.action +
        "&connection_name=" +
        props.connectionName +
        "&skill_id=" +
        state.skill.ID +
        "&lex_bot_name=" +
        state.skill.lex_bot_name,
      ...apiRequestFixedParams
    }
  };

  if (
    props.hasOwnProperty("fallbackIntentPlainTextResponse") &&
    props.hasOwnProperty("fallbackIntentRichCardResponse")
  ) {
    data.body.fallbackIntent = {
      plainTextResponse: props.fallbackIntentPlainTextResponse,
      richCardResponse: props.fallbackIntentRichCardResponse,
      intentId: props.fallbackIntentId
    };
  }

  if (_.get(props, "image", false)) {
    data.body.FormParams = data.body.FormParams + "&image=" + props.image;
  }

  if (props.collection_name !== undefined) {
    data.body.FormParams = data.body.FormParams + "&collection_name=" + props.collection_name;
  }

  let action = {
    type: CREATE_PUBLIC_CONNECTOR,
    payload: {}
  };

  try {
    const response = await API.post(config.AWS_API_NAME, "connectors/new", data);
    if (response) {
      action.payload.status = 200;
    } else {
      action.payload.status = 404;
    }
    if (props.action === "insert") {
      dispatch(triggerBotBuilding());
    }
  } catch (err) {
    action.payload.status = 500;
  }

  dispatch(action);
};

export const getOauth2ConnectionLink = connector => async (dispatch, getState) => {
  const state = getState();
  let data = {
    body: {
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      ApiCall: "OAuth2Create",
      Connection: connector,
      ...apiRequestFixedParams
    }
  };

  let action = {
    type: SET_LATEST_OAUTH_REDIRECT_LINK,
    payload: {}
  };

  try {
    const response = await API.post(config.AWS_API_NAME, "connectors/new", data);
    action.payload.status = 200;
    action.payload.oauthRedirectUrl = response.oauth2_user_redirect_link;
  } catch (err) {
    action.payload.status = 500;
    action.payload.oauthRedirectUrl = null;
  }

  dispatch(action);
};

export const setClickedConnector = connector => async (dispatch, getState) => {
  dispatch({
    type: "SET_CLICKED_CONNECTOR",
    payload: connector
  });
};

export const disableConnector = connection => async (dispatch, getState) => {
  const state = getState();

  const requestData = {
    body: {
      ApiCall: "DeleteConnection",
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      ConnectionId: connection.connectorName,
      ConnectionName: connection.connectionName,
      Skill: state.skill.appId,
      ...apiRequestFixedParams
    }
  };

  const action = {
    type: DISABLE_CONNECTOR,
    payload: { response: {} }
  };

  try {
    const response = await API.post(config.AWS_API_NAME, "connectors/enable", requestData);
    if (response.status === "success") {
      response.code = 200;
      dispatch(triggerBotBuilding());
      // dispatch(buildBot(state.skill.ID));
    }

    action.payload.response = response;
  } catch (err) {
    action.payload.response = {
      status: "error",
      code: 500,
      message: "Could not disable connector, please try again later"
    };
  }

  dispatch(action);
};

export const generatePublicChatbotSnippet = () => async (dispatch, getState) => {
  const state = getState();
  let data = {
    body: {
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      ApiCall: "GenerateHTMLPublicChatbotSnippet",
      Skill: _.omit(state.skill, ["buildInfo"]),
      ...apiRequestFixedParams
    } 
  };

  let action = {
    type: SET_PUBLIC_CHATBOT_SNIPPET_METADATA,
    payload: {}
  };

  try {
    const response = await API.post(config.AWS_API_NAME, "skills", data);
    if (response.code === 200) {
      action.payload.status = 200;
      action.payload.data = response.data;
    } else {
      action.payload.status = 500;
    }
  } catch (err) {
    action.payload.status = 500;
  }

  dispatch(action);
};

export const clearDisableConnectorResponse = () => {
  return {
    type: CLEAR_DISABLE_CONNECTOR_RESPONSE
  };
};

export const fetchActionFields = (tenant, SK) => async (dispatch, getState) => {
  const state = getState();
  let data = {
    queryStringParameters: { 
      tenant: tenant,
      SK: SK
    },
    headers: {
      Authorization: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
    }
  }

  let action = {
    type: FETCH_ACTION_FIELDS,
    payload: {}
  }; 
 
  try { 
    const response = await API.get(config.AWS_API_NAME, "form_fields", data);
    action.payload.fields = response.fields??response;
  }catch(err){
    action.payload.response = 500;
  }
  dispatch(action);
}

export const saveActionFields = (actionParams, tenant) => async (dispatch, getState) => {
  const state = getState();
  let data = {
    body: {
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      tenant: tenant,
      SK: actionParams.SK,
      title: actionParams.title,
      fields: actionParams.fields,
      lastUpdated: Date.now(),
      ...apiRequestFixedParams 
    } 
  };

  let action = {
    type: SAVE_ACTION_FIELDS,
    payload: {}
  };

  try {
    const response = await API.post(config.AWS_API_NAME, "form_fields", data);
    if (response.result === 200) {
      action.payload.status = 200;
      action.payload.data = response.data;
    } else {
      action.payload.status = 500;
    }
  } catch (err) {
    action.payload.status = 500;
  }
  dispatch(action);
}


export const fetchActionMetafields = targetIndex => async (dispatch, getState) => {

  const state = getState();
  let data = {
    queryStringParameters: { 
      query: "Opportunity"
    },
    headers: {
      Authorization: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
    }
  } 
  let endpoint = `salesforce/metafields/${targetIndex}/fields`;
  var response;
  try {
    let temp = await API.get(config.AWS_API_NAME, endpoint, data);
    response = temp.hits.hits;
    console.log(temp)
  }catch(err){
    response = err;
  } 

  dispatch({ 
    type: FETCH_ACTION_METAFIELDS,
    payload: response
  })

}

const cleanString = string => {
  return string
    .replace(/[^\w\s]/gi, "")
    .trim()
    .replace(/\s+/gi, "_")
    .toLowerCase();
};
